import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PropertyCard } from "@/components/PropertyCard";
import { Seo } from "@/components/Seo";
import { Breadcrumbs, crumbLd } from "@/components/Breadcrumbs";
import { SeoLinks } from "@/components/SeoLinks";
import { Loader2, MapPin } from "lucide-react";

export default function LocationPage() {
  const { slug } = useParams();

  const { data: locations = [], isLoading: locLoading } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations")).data,
  });
  const loc = locations.find((l) => l.slug === slug);
  const param = loc ? (loc.type === "city" ? { city: loc.name } : { locality: loc.name }) : null;

  const { data = [], isLoading } = useQuery({
    queryKey: ["location-props", slug, loc?.type],
    queryFn: async () => (await api.get("/properties", { params: param })).data,
    enabled: !!loc,
  });

  if (locLoading) {
    return <div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  }
  if (!loc) {
    return (
      <div className="px-4 max-w-3xl mx-auto py-24 text-center" data-testid="location-not-found">
        <p className="text-slate-500">We don't have listings for this location yet.</p>
        <Link to="/properties" className="text-emerald-700 font-semibold mt-3 inline-block">Browse all properties</Link>
      </div>
    );
  }

  const name = loc.name;
  const crumbs = [{ name: "Home", to: "/" }, { name: "Locations" }, { name }];

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8" data-testid="location-page">
      <Seo
        title={`Land & Plots for Sale in ${name}, Telangana | Dotlands Realty`}
        description={`Explore verified agricultural land, open plots and properties for sale in ${name}, Telangana. Browse Dotlands Realty listings with locations, sizes and prices in and around ${name}.`}
        path={`/location/${slug}`}
        jsonLd={crumbLd(crumbs)}
      />
      <Breadcrumbs items={crumbs} />
      <h1 className="text-2xl sm:text-3xl font-heading font-extrabold text-[#0E3B2E] flex items-center gap-2">
        <MapPin className="w-6 h-6 text-[#C8A24C]" /> Land &amp; Plots for Sale in {name}
      </h1>
      <p className="text-slate-600 mt-3 max-w-3xl leading-relaxed">
        Discover verified agricultural land, open plots and properties for sale in {name}. Every Dotlands Realty listing is checked by our team before it goes live, so you can explore genuine options in and around {name} with clear locations, sizes and prices.
      </p>

      <p className="text-sm text-slate-500 mt-6 mb-4" data-testid="location-count">
        {isLoading ? "Loading..." : `${data.length} ${data.length === 1 ? "property" : "properties"} in ${name}`}
      </p>

      {isLoading ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
      ) : data.length === 0 ? (
        <div className="text-center py-16 text-slate-500">No active listings in {name} right now. <Link to="/properties" className="text-emerald-700 font-semibold">See all properties</Link></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
        </div>
      )}

      <SeoLinks />
    </div>
  );
}
